import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, ChartLineUp } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePolling } from "../hooks/usePolling";

const navItems = [{ to: "/admin/rep-scorecards", label: "Rep Scorecards", icon: <ChartLineUp size={18} weight="duotone" />, testId: "nav-admin-rep-scorecards" }];

const STATUS_META = {
  green: { dot: "bg-igc-success", label: "Active" },
  yellow: { dot: "bg-amber-500", label: "Slowing" },
  red: { dot: "bg-igc-danger", label: "Inactive" },
};

export default function AdminRepScorecardsPage() {
  const [scorecards, setScorecards] = useState(null);

  const fetchScorecards = () => {
    api.get("/admin/rep-scorecards")
      .then(({ data }) => setScorecards(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(() => {
    fetchScorecards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live Sync fix (Aug 2026): this page used to fetch once on mount and never refresh, so it
  // could visibly fall behind /admin/rep-activity (which already polls every 15s) even though
  // both pull from the exact same live compute_rep_card_stats() — no separate data source, no
  // caching, just a missing poll. Matches the same 15s interval used everywhere else.
  usePolling(fetchScorecards, 15000);

  return (
    <DashboardLayout navItems={navItems} moduleName="Rep Scorecards" moduleBackPath="/admin">
      <div data-testid="admin-rep-scorecards-page">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight" title="Every rep ranked by a blended performance score of calls, deals, and commission earned.">Rep Scorecards</h1>
        </div>
        <p className="text-igc-muted text-sm mb-8">Tier, activity, deals, and commission earned — ranked by performance score. Updates automatically in the background every 15s — no refresh needed.</p>

        {!scorecards ? null : scorecards.length === 0 ? (
          <p className="text-igc-muted text-sm" data-testid="admin-rep-scorecards-empty">No reps yet.</p>
        ) : (
          <div className="bg-igc-surface border border-igc-gold/15 divide-y divide-igc-gold/10" data-testid="admin-rep-scorecards-list">
            {scorecards.map((r) => {
              const status = STATUS_META[r.activity_status] || STATUS_META.yellow;
              return (
                <div key={r.id} className="flex items-center justify-between px-5 py-4 text-sm" data-testid={`admin-rep-scorecard-row-${r.id}`}>
                  <div className="flex items-center gap-4">
                    <span className="font-serif text-lg text-igc-goldText w-8">#{r.rank}</span>
                    <div>
                      <p className="text-igc-cream">{r.display_name} <span className="text-igc-muted text-xs">{r.user_id}</span></p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`w-2 h-2 rounded-full ${status.dot}`} />
                        <span className="text-igc-muted text-[10px] uppercase tracking-wide">{status.label}</span>
                        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 ${r.tier === "full_partner" ? "bg-igc-success/10 text-igc-success" : "bg-igc-gold/15 text-igc-goldText"}`}>
                          {r.tier === "full_partner" ? "Full Partner" : "Starter"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-8 text-xs">
                    <div className="text-center" title="How many calls this rep has attempted so far this week.">
                      <p className="font-serif text-lg text-igc-cream">{r.calls_attempted_week}</p>
                      <p className="text-igc-muted uppercase tracking-widest">Calls This Week</p>
                    </div>
                    <div className="text-center" title="How many deals this rep has submitted for commission.">
                      <p className="font-serif text-lg text-igc-cream">{r.deals_submitted}</p>
                      <p className="text-igc-muted uppercase tracking-widest">Referrals</p>
                    </div>
                    <div className="text-center" title="Total commission this rep has earned to date.">
                      <p className="font-serif text-lg text-igc-goldText">${r.commissions_earned?.toLocaleString?.() ?? r.commissions_earned}</p>
                      <p className="text-igc-muted uppercase tracking-widest">Earned</p>
                    </div>
                    <div className="text-center flex items-center gap-1" title="A blended score combining calls, deals closed, and commission earned — higher is better.">
                      <Trophy size={16} weight="fill" className="text-igc-goldText" />
                      <span className="font-serif text-lg text-igc-cream">{r.performance_score}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
